import * as Yup from 'yup';
import { transformCaseNumber } from '../pages/create-notice/step-pages/components/utility-componet-functions/court-page/utils';
import { areCaseNumbersUnique, dateValidationTest, isValidZipCode } from './utility-functions';
import parsePhoneNumberFromString from 'libphonenumber-js';

const jointCaseNumbersSchema = Yup.array().of(
  Yup.object()
    .shape({
      id: Yup.string().required(),
      value: Yup.string().required(),
      textfieldValue: Yup.string().required(),
      debtorName: Yup.string(),
    })
    .test('has-value', 'Joint Case Number must not be empty', ({ value, id }, { createError }) => {
      if (!value) {
        return createError({
          path: `jointCaseNumber-${id}`,
          message: 'Joint Case Number is required',
        });
      }
      return true;
    }),
);

export const courtCaseSchema = Yup.object()
  .shape({
    caseNumber: Yup.object().shape({
      textfieldValue: Yup.string().required('Case Number is required'),
      value: Yup.string().required(),
    }),
    jointCaseNumbers: jointCaseNumbersSchema,
    courtState: Yup.string().required('Court state is required'),
    courtDistrict: Yup.string().required('Court district is required'),
    isECFUnavailable: Yup.boolean(),
    caseChapter: Yup.string().when('isECFUnavailable', ([isECFUnavailable], schema) => {
      return isECFUnavailable ? schema.required('Case chapter is required') : schema;
    }),
    caseData: Yup.object().when('isECFUnavailable', ([isECFUnavailable], schema) =>
      isECFUnavailable
        ? schema.shape({
            debtorName: Yup.string().required('Debtor name is required'),
          })
        : schema,
    ),
    caseSource: Yup.object()
      .shape({
        userSupplied: Yup.boolean(),
        importCaseParties: Yup.boolean(),
        masterMailingMatrix: Yup.boolean(),
      })
      .test('at-least-one-selected', 'Please choose at least one document source!', function (value) {
        // Ensure at least one of the values is true
        return value.userSupplied || value.masterMailingMatrix || value.importCaseParties;
      }),
    courtDivision: Yup.string(),
    caseNumbers: Yup.array(),
    adversaryProceeding: Yup.object().shape({
      isChecked: Yup.boolean(),
      adversaryNumber: Yup.string().when('isChecked', ([isChecked], schema) => {
        return isChecked ? schema.required('Please enter a valid adversary number') : schema;
      }),
    }),
    multipleCaseNumbersToChoose: Yup.object(),
  })
  .test('validate-chosen-number', 'Chose Case Number', (value, { createError }) => {
    // if any case number value is equal to a key in multipleCaseNumbersToChoose, means no option has been chosen
    const { multipleCaseNumbersToChoose, caseNumber, jointCaseNumbers } = value;
    if (Object.keys(multipleCaseNumbersToChoose).length > 0) {
      const allCaseNumbers = [caseNumber.value, ...(jointCaseNumbers ?? []).map((item) => item.value)];
      const numbersWithMultipleOptions = Object.keys(multipleCaseNumbersToChoose).map((key) =>
        key === 'mainCaseNumber'
          ? caseNumber.textfieldValue
          : jointCaseNumbers!.find((i) => i.id === key)!.textfieldValue,
      );
      if (numbersWithMultipleOptions.some((caseNumber) => allCaseNumbers.includes(caseNumber))) {
        return createError({ path: 'choseCaseNumber', message: 'Chose Case Number' });
      }
    }
    return true;
  });

export const courtLegalSchema = Yup.object()
  .shape({
    mailingType: Yup.string().required('Mailing Type is required'),
    caseNumber: Yup.object().shape({
      textfieldValue: Yup.string().required('Case Number is required'),
      value: Yup.string().required(),
    }),
    courtState: Yup.string(),
    courtDistrict: Yup.string(),
    jointCaseNumbers: jointCaseNumbersSchema,
    caseSource: Yup.object()
      .shape({
        userSupplied: Yup.boolean(),
      })
      .test('at-least-one-selected', 'Please choose at least one document source!', function (value) {
        return value.userSupplied;
      }),
    courtDivision: Yup.string(),
    caseNumbers: Yup.array(),
    legalCaseFields: Yup.object().shape({
      plaintiff: Yup.string(),
      defendant: Yup.string(),
      estatePartyName: Yup.string(),
    }),
    courtJurisdiction: Yup.object().shape({
      county: Yup.string(),
      state: Yup.string(),
      division: Yup.string(),
    }),
  })
  .test('legal-mailing', function (values) {
    const { mailingType, courtJurisdiction, courtState, courtDistrict, legalCaseFields, jointCaseNumbers, caseNumber } =
      values;

    const errors: any[] = [];

    const combinedCaseNumbers = [caseNumber.textfieldValue, ...(jointCaseNumbers?.map((item) => item.value) ?? [])];

    if (!areCaseNumbersUnique(combinedCaseNumbers)) {
      errors.push(
        this.createError({
          path: 'caseNumbers',
          message: 'Some case numbers are duplicated',
        }),
      );
    }

    const checkCourtData = () => {
      if (courtState === '') {
        errors.push(
          this.createError({
            path: 'courtState',
            message: 'Court State is required',
          }),
        );
      }

      if (courtDistrict === '') {
        errors.push(
          this.createError({
            path: 'courtDistrict',
            message: 'Court District is required',
          }),
        );
      }
    };

    const checkCourtJurisdiction = () => {
      const hasAtLeastOneValueProvided = Object.values(courtJurisdiction).some((value) => value !== '');

      if (!hasAtLeastOneValueProvided) {
        errors.push(
          this.createError({
            path: `courtJurisdiction`,
            message: `Provide at least one field.`,
          }),
        );
      }
    };

    const checkLegalCaseFields = () => {
      if (mailingType !== 'Probate Estate' && (legalCaseFields.plaintiff ?? '').trim() === '') {
        errors.push(this.createError({ path: 'plaintiff', message: 'Plaintiff is required' }));
      }

      if (
        (mailingType === 'Federal Court' || mailingType === 'State Court') &&
        (legalCaseFields.defendant ?? '').trim() === ''
      ) {
        errors.push(this.createError({ path: 'defendant', message: 'Defendant is required' }));
      }

      if (mailingType === 'Probate Estate' && legalCaseFields.estatePartyName === '') {
        errors.push(this.createError({ path: 'estatePartyName', message: 'Estate Party Name required' }));
      }
    };

    if (mailingType !== 'Federal Court') {
      checkCourtJurisdiction();
    } else {
      checkCourtData();
    }

    checkLegalCaseFields();

    if (errors.length > 0) {
      throw new Yup.ValidationError(errors);
    }
    return true;
  });

export const directMailingTypeSchema = Yup.object().shape({
  directMailingType: Yup.object().shape({
    type: Yup.string().required('Direct Mailing Type is required'),
  }),
});

const pdfItemWithDescriptionSchema = Yup.object().shape({
  id: Yup.string(),
  documentDescription: Yup.string()
    .max(85, 'Description cannot be longer than 85 symbols')
    .test('description-required', function (value) {
      const { id } = this.parent;
      const pathArray = this.path.split('.');
      const index = pathArray[pathArray.length - 2];
      return value && value.trim()
        ? true
        : this.createError({
            path: `uploadedFiles[${index}].description`,
            message: `Document Description is required`,
            params: { id: id, validationType: 'documents' },
          });
    }),
});

export const documentsSchema = Yup.object()
  .shape({
    uploadedPdfs: Yup.array().of(pdfItemWithDescriptionSchema),
    uploadedBallot: Yup.array().of(pdfItemWithDescriptionSchema),
    uploadedProofOfClaim: Yup.array().of(pdfItemWithDescriptionSchema),
  })
  .test('at-least-one-document', 'Please upload at least one document', function (values, { createError }) {
    const { uploadedBallot = [], uploadedProofOfClaim = [], uploadedPdfs = [] } = values;
    if ([...uploadedBallot, ...uploadedProofOfClaim, ...uploadedPdfs].length === 0) {
      return createError({ path: 'uploadedPdfs', message: 'Please upload at least one document' });
    }
    return true;
  });

export const caseDocketSchema = Yup.object().shape({
  caseDocketEntries: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().required(),
        isChecked: Yup.boolean().required(),
        // docketDescription: Yup.string()
        //   .max(85, 'Description cannot be longer than 85 characters')
        //   .when(['isChecked', 'id'], function (args, schema) {
        //     const isChecked = args[0];
        //     const id = args[1];

        //     return isChecked
        //       ? schema.required(`Document description is required-${id}`)
        //       : schema;
        //   }),
      }),
    )
    .test('at-least-one-checked', 'Please select at least one document', function (caseDocketEntries) {
      return caseDocketEntries?.some((entry) => entry.isChecked);
    })
    .required('You must select at least one PDF'),
});

export const addressSchema = Yup.object()
  .shape({
    caseParties: Yup.array().of(
      Yup.object().shape({
        isFirstClass: Yup.boolean().required(),
        isOvernight: Yup.boolean().required(),
        isCertified: Yup.boolean().required(),
        isCmEcf: Yup.boolean().required(),
      }),
    ),
    creditors: Yup.array().of(
      Yup.object().shape({
        isFirstClass: Yup.boolean().required(),
        isOvernight: Yup.boolean().required(),
        isCertified: Yup.boolean().required(),
        isCmEcf: Yup.boolean().required(),
      }),
    ),
    attorneys: Yup.array().of(
      Yup.object().shape({
        isFirstClass: Yup.boolean().required(),
        isOvernight: Yup.boolean().required(),
        isCertified: Yup.boolean().required(),
        isCmEcf: Yup.boolean().required(),
      }),
    ),
    caseSource: Yup.object().shape({
      userSupplied: Yup.boolean(),
      masterMailingMatrix: Yup.boolean(),
      importCaseParties: Yup.boolean(),
    }),
    userUploadedAddresses: Yup.array().of(
      Yup.object().shape({
        isFirstClass: Yup.boolean().required(),
        isOvernight: Yup.boolean().required(),
        isCertified: Yup.boolean().required(),
        isCmEcf: Yup.boolean().required(),
      }),
    ),
  })
  .test('at-least-one-selected-in-any-array', 'At least one party in any array must be selected', function (values) {
    const { caseParties = [], creditors = [], attorneys = [], userUploadedAddresses = [], caseSource } = values;

    const { userSupplied } = caseSource || {};
    const combinedParties = [...caseParties, ...creditors, ...attorneys];

    const hasSelectedProperty = (arr: any[]) =>
      arr.some((party) => party.isFirstClass || party.isCertified || party.isOvernight || party.isCmEcf);

    if (userSupplied) {
      return hasSelectedProperty(userUploadedAddresses)
        ? true
        : this.createError({
            path: 'userUploadedAddresses',
            message: 'Please select at least one recipient.',
          });
    }

    const isValidCombinedParties = hasSelectedProperty(combinedParties);

    if (!isValidCombinedParties) {
      return this.createError({
        path: 'combinedParties',
        message: 'Please select at least one recipient',
      });
    }

    return true;
  });

export const court341Schema = Yup.object().shape({
  caseNumber: Yup.string()
    .required('Case number is required')
    .test('is-valid-case-number', 'Invalid case number format', function (value) {
      if (typeof value === 'string') {
        try {
          // Attempt to transform the case number
          const transformedValue = transformCaseNumber(value);

          // If the transformation is successful, return true
          return !!transformedValue;
        } catch (error) {
          // If transformation fails, return false to trigger the custom error message
          return false;
        }
      }
      // If the value is not a string, return false to indicate invalid input
      return false;
    }),
  tate: Yup.string().required('Court state is required'),
  courtDistrict: Yup.string().required('Court division is required'),
  courtDivision: Yup.string(),
  caseChapter: Yup.string().required('Chapter is required.'),
  caseChapterFillingDate: Yup.string().concat(dateValidationTest).required('Filling date is required.'),
  bankruptcyType: Yup.string().required('Bankruptcy type is required.'),
  meetingLocation: Yup.string().required('Location is required'),
  meetingDate: Yup.string().concat(dateValidationTest).required('Date is required'),
  meetingTime: Yup.string().required('Time is required'),
});

export const debtor341SchemaCorporation = Yup.object().shape({
  debtors: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      ein: Yup.string().test('is-valid-ein', 'EIN must be in format XX-XXXXXXX', function (value) {
        const { id } = this.parent;

        if (!value) {
          return this.createError({
            path: `debtors-${id}`,
            message: 'EIN is required',
          });
        }

        const matches = /^\d{2}-\d{7}$/.test(value);

        if (!matches) {
          return this.createError({
            path: `debtors-${id}`,
            message: 'EIN must be in format XX-XXXXXXX',
          });
        }

        return true;
      }),
    }),
  ),
  bankruptcyClerk: Yup.object()
    .shape({
      addressOne: Yup.string(),
    })
    .test('is-bankruptcyclerk-added', 'Please add a bankruptcy clerk office data', function (data) {
      // 'data' is the value of 'addressOne'
      return data.addressOne !== '';
    }),
});

export const debtor341SchemaIndividual = Yup.object().shape({
  debtors: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      ein: Yup.string().test('is-valid-ein', 'EIN must be in format XX-XXXXXXX', function (value) {
        const { id } = this.parent;

        if (!value) {
          return this.createError({
            path: `debtors-${id}`,
            message: 'EIN is required',
          });
        }

        const matches = /^\d{2}-\d{7}$/.test(value);

        if (!matches) {
          return this.createError({
            path: `debtors-${id}`,
            message: 'EIN must be in format XX-XXXXXXX',
          });
        }

        return true;
      }),
      ssnOrItin: Yup.string().test('is-valid-ssnOrItin', 'Must provide last 4 digits', function (value) {
        const { id } = this.parent;

        if (!value) {
          return this.createError({
            path: `debtors-${id}-ssn`,
            message: 'SSN or ITIN Digits are required',
          });
        }

        const matches = /^\d{4}$/.test(value);

        if (!matches) {
          return this.createError({
            path: `debtors-${id}-ssn`,
            message: 'SSN/ITIN must be in format XXXX',
          });
        }

        return true;
      }),
    }),
  ),
  bankruptcyClerk: Yup.object()
    .shape({
      addressOne: Yup.string(),
    })
    .test('is-bankruptcyclerk-added', 'Please add a bankruptcy clerk office data', function (data) {
      // 'data' is the value of 'addressOne'
      return data.addressOne !== '';
    }),
});
// Overall schema for the whole state
const itemSchema = Yup.object()
  .shape({
    id: Yup.string().required(),
    isFirstClass: Yup.boolean().required(),
    isCertified: Yup.boolean().required(),
    isOvernight: Yup.boolean().required(),
    newJerseyRelationshipOfParty: Yup.string(),
  })
  .test(
    'validate-relationToParty',
    'Relation to Party must not be empty if any of isFirstClass, isCertified, or Priority Express is true',
    function (value) {
      const { id, isFirstClass, isCertified, isOvernight, newJerseyRelationshipOfParty } = value;

      if ((isFirstClass || isCertified || isOvernight) && !newJerseyRelationshipOfParty?.trim() && id !== undefined) {
        return this.createError({
          path: `newJersey-${id}`,
          message: 'Relation to party is required.',
        });
      }
      return true;
    },
  );

const aboutTheCaseProceedingSchema = Yup.array()
  .min(1, 'Please select at least one Chapter')
  .test('valid-chapter-selection', 'At least one valid chapter must be selected', function (value) {
    const validChapters = [
      'Chapter 7 case (indicate below if subject to limited noticing; check all that are applicable)',
      'Chapter 9 case (indicate below if subject to limited noticing)',
      'Chapter 11 (indicate below if subject to limited noticing)',
      'Chapter 12 or 13 case (indicate below if subject to limited noticing; check all that are applicable)',
      'Chapter 15 case',
      'Adversary Proceeding',
    ];

    if (!value) {
      value = [];
    }

    const validChaptersInArray = value.filter((val) => validChapters.includes(val));

    if (validChaptersInArray.length === 0) {
      return this.createError({
        path: 'edcaCaseProceeding',
        message: 'Please select at least one chapter',
      });
    }

    if (validChaptersInArray.length > 1) {
      return this.createError({
        path: 'edcaCaseProceeding',
        message: 'Only one chapter can be selected at a time',
      });
    }

    return true;
  });

const returnAddressesSchema = Yup.array().of(
  Yup.object().shape({
    addressName: Yup.string().nullable(),
    addressOne: Yup.string(),
    addressTwo: Yup.string().nullable(),
    city: Yup.string(),
    firmName: Yup.string().nullable(),
    isDefault: Yup.boolean(),
    selected: Yup.boolean(),
  }),
);

const returnEnvelopeSchema = Yup.object()
  .shape({
    name: Yup.string(),
    office: Yup.string(),
    address: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zipCode: Yup.string(),
  })
  .when('includeReturnEnvelope', (includeReturnEnvelope, schema) => {
    const isValid = includeReturnEnvelope[0];
    return isValid
      ? schema
          .shape({
            name: Yup.string().required('Name is required'),
            office: Yup.string().required('Office is required'),
            address: Yup.string().required('Address is required'),
            city: Yup.string().required('City is required'),
            state: Yup.string().required('State is required'),
            zipCode: Yup.string().required('Zip Code required'),
          })
          .test('is-zip-valid', (value, { createError }) => {
            if (value.state && value.zipCode && !isValidZipCode(value.state, value.zipCode)) {
              return createError({
                path: 'returnEnvelope.zipCode',
                message: 'Invalid Zip Code',
              });
            }
            return true;
          })
      : schema;
  });

const certificatePreferencesSchema = Yup.object()
  .shape({
    shouldAttachDocs: Yup.boolean(),
    shouldAddHearingInfo: Yup.boolean(),
    shouldIncludeNEF: Yup.boolean(),
    shouldIncludeCustomName: Yup.boolean(),
    customFileName: Yup.string(),
    hearingInformation: Yup.object().shape({
      hearingDate: Yup.string(),
      hearingTime: Yup.string(),
      hearingLocation: Yup.string(),
      responseDate: Yup.string(),
    }),
  })
  .test('custom-file-name', 'Custom file name is required', (value, { createError }) => {
    const { shouldIncludeCustomName, customFileName } = value;
    if (shouldIncludeCustomName && !customFileName) {
      return createError({
        path: 'customFileName',
        message: 'Name is required. 85 character limit, no special symbols allowed',
      });
    }
    return true;
  });

const signatureBlocksSchema = Yup.array().of(
  Yup.object().shape({
    fullName: Yup.string(),
    barNumber: Yup.string().nullable(),
    firmName: Yup.string().nullable(),
    clientRole: Yup.string(),
    addressOne: Yup.string(),
    addressTwo: Yup.string().nullable(),
    city: Yup.string(),
    state: Yup.string(),
    country: Yup.string(),
    zipCode: Yup.string(),
    phone: Yup.string(),
    email: Yup.string().nullable(),
    fax: Yup.string().nullable(),
    isDefault: Yup.boolean(),
    selected: Yup.boolean(),
  }),
);
const servedPartiesSchema = Yup.object({
  debtor: Yup.boolean(),
  plaintiff: Yup.boolean(),
  defendant: Yup.boolean(),
  allCommitteeMembers: Yup.boolean(),
  equitySecurityHolders: Yup.boolean(),
  allCreditorsAndPartiesInInterestNoticeOfHearingOnly: Yup.boolean(),
  onlyCreditorsThatHaveFiledClaimsNoticeOfHearingOnly: Yup.boolean(),
  allCreditorsAndPartiesInInterest: Yup.boolean(),
  fewerThanAllCreditors: Yup.object({
    creditorsThatHaveFiledClaims: Yup.boolean(),
    creditorsHoldingAllowedSecuredClaims: Yup.boolean(),
    creditorsHoldingAllowedPriorityUnsecuredClaims: Yup.boolean(),
    creditorsHoldingLeasesOrExecutoryContractsThatHaveBeenAssumed: Yup.boolean(),
    twentyLargestCreditors: Yup.boolean(),
  }),
  administrativeClaimants: Yup.boolean(),
  otherPartyInInterest: Yup.boolean(),
  isFewerThanAllCreditorsChecked: Yup.boolean(),
}).test('at-least-one-true', 'At least one option must be true', function (value, context) {
  const { fewerThanAllCreditors } = value;

  const flatValues = {
    ...value,
    ...fewerThanAllCreditors,
  };

  if (!Object.values(flatValues).some((val) => val === true)) {
    return context.createError({
      path: 'edcaServedParty',
      message: 'At least one option must be selected',
    });
  }

  if (value.isFewerThanAllCreditorsChecked && !Object.values(fewerThanAllCreditors).some((val) => val)) {
    return context.createError({
      path: 'edcaServedParty',
      message: 'At least one valid party must be selected when "Fewer than all creditors" is chosen',
    });
  }

  return true;
});

// Reusable array schema for caseParties, creditors, and attorneys
const arraySchema = Yup.array().of(itemSchema);

export const orderPreferenceSchema = Yup.object()
  .shape({
    directMailingType: Yup.object()
      .shape({
        type: Yup.string(),
      })
      .nullable(),
    includeReturnEnvelope: Yup.boolean(),
    includeCertificateOfService: Yup.boolean().nullable(),
    returnEnvelope: returnEnvelopeSchema,
    certificatePreferences: certificatePreferencesSchema,
    returnAddresses: returnAddressesSchema,
    signatureBlocks: signatureBlocksSchema,
    jointCaseNumbers: jointCaseNumbersSchema,
    caseNumber: Yup.object().shape({ textfieldValue: Yup.string().nullable(), value: Yup.string().nullable() }),
    isCaseNumberChecked: Yup.boolean().nullable(),
  })
  .test('at-least-one-hearing', (value, { createError }) => {
    const { includeCertificateOfService, certificatePreferences } = value;

    if (includeCertificateOfService && certificatePreferences.shouldAddHearingInfo) {
      if (
        !certificatePreferences.hearingInformation.hearingDate &&
        !certificatePreferences.hearingInformation.hearingLocation &&
        !certificatePreferences.hearingInformation.hearingTime &&
        !certificatePreferences.hearingInformation.responseDate
      ) {
        return createError({
          path: 'hearingInformation',
          message: `To include hearing information, please provide at least one field.`,
        });
      }
    }
    return true;
  })
  .test('select-signature-if-certificate-included', (value, { createError }) => {
    const { includeCertificateOfService, signatureBlocks } = value;

    if (includeCertificateOfService) {
      const hasSelectedSignature = signatureBlocks?.find((item) => item.selected);
      if (hasSelectedSignature) return true;
      return createError({
        path: 'selectedSignature',
        message: 'Select signature to proceed',
      });
    }
    return true;
  })
  .test('selected-return-address', (value, { createError }) => {
    const { directMailingType, returnAddresses } = value;
    if (!directMailingType || directMailingType.type === 'direct_mail_non_personalized') {
      const hasSelectedReturnAddress = returnAddresses?.find((address) => address.selected);
      if (hasSelectedReturnAddress) {
        return true;
      }
      return createError({
        path: 'selectedReturnAddress',
        message: 'Select return address to proceed',
      });
    }
    return true;
  })
  .test('are-case-numbers-unique', (value, { createError }) => {
    const { directMailingType, caseNumber, jointCaseNumbers } = value;
    if (!directMailingType) {
      return true;
    } else {
      const combinedCaseNumbers = jointCaseNumbers?.map((item) => item.value) ?? [];
      if (caseNumber.value) combinedCaseNumbers.push(caseNumber.value);
      if (!areCaseNumbersUnique(combinedCaseNumbers)) {
        return createError({
          path: 'caseNumbers',
          message: 'Some case numbers are duplicated',
        });
      }
    }
    return true;
  })
  .test('is-case-number-empty', (value, { createError }) => {
    const { directMailingType, caseNumber, isCaseNumberChecked } = value;
    if (directMailingType?.type && isCaseNumberChecked && !caseNumber.value) {
      return createError({
        path: 'caseNumber',
        message: 'Case number is required',
      });
    }
    return true;
  });

export const returnAddressSchema = Yup.object()
  .shape({
    firmName: Yup.string(),
    addressName: Yup.string(),
    addressOne: Yup.string().required('Address one is required.'),
    city: Yup.string().required('City is required.'),
    state: Yup.string().required('State is required.'),
    zipCode: Yup.string().required('Zip code is required.'),
    country: Yup.string().required('Country is required'),
  })
  .test('at-least-one', function (value) {
    if (value.firmName !== '' || value.addressName !== '') {
      return true;
    } else {
      return this.createError({
        path: 'addressName',
        message: `Please Provide a Return Address Name or Firm Name`,
      });
    }
  })
  .test('is-zip-valid', (value, { createError }) => {
    if (
      value.country === 'United States' &&
      value.state &&
      value.zipCode &&
      !isValidZipCode(value.state, value.zipCode)
    ) {
      return createError({
        path: 'zipCode',
        message: 'Invalid Zip Code',
      });
    }
    return true;
  });

export const newJerseyPreferencesSchema = orderPreferenceSchema.shape({
  caseParties: arraySchema,
  creditors: arraySchema,
  attorneys: arraySchema,
  userUploadedAddresses: arraySchema,
  newJerseyAttestation: Yup.object()
    .shape({
      attestationType: Yup.string(),
      directClientName: Yup.string(),
      secondaryClientName: Yup.string(),
    })
    .test('is-client-name-and-attestation-type-selected', 'Selection is required', function (value) {
      const { attestationType, directClientName, secondaryClientName } = value;

      if (attestationType === '') {
        return this.createError({
          message: 'Please select an option',
          path: 'attestationType',
        });
      }

      if (attestationType === 'attorney') {
        if (directClientName === '') {
          return this.createError({
            message: 'Please input client name',
            path: 'directClientName',
          });
        } else {
          return true;
        }
      }

      if (attestationType === 'secretary') {
        if (secondaryClientName === '') {
          return this.createError({
            message: 'Please input client name',
            path: 'secondaryClientName',
          });
        } else {
          return true;
        }
      }
    }),
});

export const EDCAPreferencesSchema = orderPreferenceSchema
  .shape({
    californiaServiceDelivery: Yup.object().shape({
      is6A1Selected: Yup.boolean(),
      is6A2Selected: Yup.boolean(),
      is6B1Selected: Yup.boolean(),
      is6B2Selected: Yup.boolean(),
      is6B3Selected: Yup.boolean(),
      serviceCapacity: Yup.string(),
    }),
    edcaCaseProceeding: aboutTheCaseProceedingSchema,
    edcaServedParty: servedPartiesSchema,
  })
  .test(
    'at-least-one-rule-exists',
    'At least one of rule7004 or rule5 must be present with at least one entry',
    function (value) {
      const { californiaServiceDelivery } = value;
      for (const [_, props] of Object.entries(californiaServiceDelivery) as [id: any, props: any]) {
        if (
          !props.is6A1Selected &&
          !props.is6A2Selected &&
          !props.is6B1Selected &&
          !props.is6B2Selected &&
          !props.is6B3Selected
        ) {
          return this.createError({
            path: 'rules',
            message: 'At least one of Rule 7004 or Rule 5 must be present in each entry',
          });
        }

        if ((props.is6A1Selected || props.is6A2Selected) && props.serviceCapacity === '') {
          return this.createError({
            path: 'serviceCapacity',
            message: `Must select party if 6A1 or 6A2 is checked to proceed`,
          });
        }
      }

      return true;
    },
  );

export const nonSpecialPreferencesSchema = Yup.object().shape({});

export const reviewAndSubmitSchema = Yup.object().shape({
  paymentMethodId: Yup.string().required('Payment Method is required'),
});

export const signatureBlockSchema = Yup.object()
  .shape({
    fullName: Yup.string().required('Full name is required.'),
    barNumber: Yup.string().nullable(),
    firmName: Yup.string().nullable(),
    clientRole: Yup.string().required('Role is required.'),
    addressOne: Yup.string().required('Address one is required.'),
    city: Yup.string().required('City is required.'),
    state: Yup.string().required('State is required.'),
    zipCode: Yup.string().required('Zip code is required.'),
    country: Yup.string().required('Country is required.'),
    phone: Yup.string().required('Phone number is required.'),
    email: Yup.string()
      .notRequired()
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, {
        message: 'Wrong format',
        excludeEmptyString: true,
      }),
    fax: Yup.string().nullable(),
  })
  .test('is-zip-valid', (value, { createError }) => {
    if (
      value.country === 'United States' &&
      value.state &&
      value.zipCode &&
      !isValidZipCode(value.state, value.zipCode)
    ) {
      return createError({
        path: 'zipCode',
        message: 'Invalid Zip Code',
      });
    }
    return true;
  })
  .test('is-valid-phone', (value, { createError }) => {
    const formattedPhone = parsePhoneNumberFromString(value.phone, 'US');
    if (!formattedPhone?.isValid()) {
      return createError({
        path: 'phone',
        message: 'Wrong phone format'
      })
    }
    return true;
  })
  ;

export const bankruptcyClerkSchema = Yup.object().shape({
  fullName: Yup.string().required('Full name is required.'),
  addressOne: Yup.string().required('Address one is required.'),
  city: Yup.string().required('City is required.'),
  state: Yup.string().required('State is required.'),
  zipCode: Yup.string().required('Zip code is required.'),
  phone: Yup.string().required('Phone number is required.'),
  email: Yup.string().required('Email is required.'),
  fax: Yup.string().required('Fax is required.'),
  webAddress: Yup.string().required('Website address is required'),
  workingHours: Yup.string().required('Working hours is required'),
});

export const creditorsProofOfClaim = Yup.string().required('Deadline date is required');
export const governmentProofOfClaim = Yup.string().required('Deadline date is required');

//special mapper for standalone test cases in order to build a schema for each 341 form
const schema341SpecialMap: Record<string, Yup.AnySchema> = {
  creditorsProofOfClaim: Yup.string().concat(dateValidationTest).required('Deadline date is required'),
  governmentProofOfClaim: Yup.string().concat(dateValidationTest).required('Deadline date is required'),
  dischargeable: Yup.string().concat(dateValidationTest).required('Deadline date is required'),
  presumptionOfAbuse: Yup.string().required('This selection is required.'),
  fillingPlanSelected: Yup.string().required('This selection is required.'),
  hearingDate: Yup.string().when('fillingPlanSelected', ([fillingPlanSelected], schema) =>
    fillingPlanSelected === 'The debtor has filed a plan, which is attached. The hearing on confirmation will be held'
      ? schema.concat(dateValidationTest).required('Hearing Date is required')
      : schema,
  ),
  hearingTime: Yup.string().when('fillingPlanSelected', ([fillingPlanSelected], schema) =>
    fillingPlanSelected === 'The debtor has filed a plan, which is attached. The hearing on confirmation will be held'
      ? schema.required('Hearing Time is required')
      : schema,
  ),
  hearingLocation: Yup.string().when('fillingPlanSelected', ([fillingPlanSelected], schema) =>
    fillingPlanSelected === 'The debtor has filed a plan, which is attached. The hearing on confirmation will be held'
      ? schema.required('Hearing Location is required')
      : schema,
  ),
  deadlineProofOfClaimSelected: Yup.string().required('This selection is required'),
  deadlineForFillingProofOfClaimSpecial: Yup.string().when(
    'deadlineProofOfClaimSelected',
    ([deadlineProofOfClaimSelected], schema) =>
      deadlineProofOfClaimSelected === 'date'
        ? schema.concat(dateValidationTest).required('Deadline date is required')
        : schema,
  ),
};

export const createSchema = (schemaNames: string[]) => {
  const schemas: Record<string, Yup.AnySchema> = {};

  schemaNames.forEach((name) => {
    const schema = schema341SpecialMap[name];
    if (schema) {
      schemas[name] = schema;
    } else {
      console.warn(`Schema for ${name} does not exist`);
    }
  });

  return Yup.object(schemas);
};

export const addNewAddressSchema = Yup.object()
  .shape({
    name: Yup.string().required('Recipient name is required.'),
    addressOne: Yup.string().required('Address 1 is required.'),
    addressTwo: Yup.string(),
    addressThree: Yup.string(),
    addressFour: Yup.string(),
    addressFive: Yup.string(),
    city: Yup.string().when('$isRequired', ([isRequired], schema) =>
      isRequired ? schema.required('City is required.') : schema,
    ),
    state: Yup.string().when('$isRequired', ([isRequired], schema) =>
      isRequired ? schema.required('State is required.') : schema,
    ),
    zip: Yup.string().when('$isRequired', ([isRequired], schema) =>
      isRequired ? schema.required('Zip is required.') : schema,
    ),
    country: Yup.string().when('$isRequired', ([isRequired], schema) =>
      isRequired ? schema.required('Country is required.') : schema,
    ),
    isFirstClass: Yup.boolean(),
    isCertified: Yup.boolean(),
    isOvernight: Yup.boolean(),
    isCmEcf: Yup.boolean(),
  })
  .test(
    'at-least-one-true',
    'At least one of isFirstClass, isCertified, or Priority Express should be true.',
    function (value) {
      const { isFirstClass, isCertified, isOvernight, isCmEcf } = value;
      const trueCount = [isFirstClass, isCertified, isOvernight, isCmEcf].filter(Boolean).length;
      if (trueCount >= 1) {
        return true;
      } else {
        return this.createError({
          path: 'mailingType',
          message: `Please select one of the mailing types`,
        });
      }
    },
  )
  .test('is-zip-valid', (value, { createError }) => {
    if (value.country === 'United States' && value.state && value.zip && !isValidZipCode(value.state, value.zip)) {
      return createError({
        path: 'zip',
        message: 'Invalid Zip Code',
      });
    }
    return true;
  });

export const companyInformationSchema = Yup.object()
  .shape({
    organizationType: Yup.string().required('What best describes your firm is required'),
    companyName: Yup.string().required('Company Name is required'),
    countryCode: Yup.string().required(),
    companyPhoneNumber: Yup.string()
      .required('Company phone number is required')
      .test('is-valid-phone', 'Wrong phone number format', function (value) {
        const { countryCode } = this.parent;
        if (!value || !countryCode) return false;

        try {
          const phoneNumber = parsePhoneNumberFromString(value, countryCode);
          return phoneNumber?.isValid() || false;
        } catch (e) {
          return false;
        }
      }),
    addressOne: Yup.string().required('Address 1 is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zip: Yup.string().required('ZIP code is required'),
    surveyAnswer: Yup.string().nullable(),
    surveyAnswerDetail: Yup.string().nullable(),
  })
  .test('is-zip-valid', (value, { createError }) => {
    if (value.countryCode === 'US' && value.state && value.zip && !isValidZipCode(value.state, value.zip)) {
      return createError({
        path: 'zip',
        message: 'Invalid Zip Code',
      });
    }
    return true;
  });
